import { Color, GStack, GetVec3, Matrix4x4, Vector2, Vector3 } from "./types";

export function showError(errorText: string) {
    console.log(errorText);
}

export function MathAlignToPowerOf2(num: number): number {
    return Math.pow(2, Math.ceil(Math.log2(num)));
}

export function MathGetVec2Length(vec2: Vector2) {
    return Math.sqrt(vec2.x * vec2.x + vec2.y * vec2.y);
}
export function MathGetVec3Length(vec: Vector3) {
    return Math.sqrt(vec.x * vec.x + vec.y * vec.y + vec.z * vec.z);
}

export function MathClamp(value: number, min: number, max: number) {
    return Math.min(Math.max(value, min), max);
}

export function MathImpulse(x: number, k = 5) {
    const h = k * x;
    return h * Math.exp(1.0 - h);
}

export function MathSignedMax(value: number, max: number) {
    if (value < 0.0) {
        return Math.min(value, -max);
    } else {
        return Math.max(value, max);
    }
}
export function MathVector2Normalize(vec: Vector2) {
    const length = Math.sqrt(vec.x * vec.x + vec.y * vec.y);
    if (length === 0) {
        // Avoid division by zero
        return { x: 0, y: 0 };
    } else {
        return { x: vec.x / length, y: vec.y / length };
    }
}
export function MathVector3Normalize(vec: Vector3) {
    const length = Math.sqrt(vec.x * vec.x + vec.y * vec.y + vec.z * vec.z);
    if (length === 0) {
        // Avoid division by zero
        return GetVec3(0, 0, 0);
    } else {
        return GetVec3(vec.x / length, vec.y / length, vec.z / length);
    }
}

export function Vec2Negate(a: Vector2, b: Vector2): Vector2 {
    return { x: a.x - b.x, y: a.y - b.y };
}
export function Vec2Add(a: Vector2, b: Vector2): Vector2 {
    return { x: a.x + b.x, y: a.y + b.y };
}
export function Vec2Multiply(vec: Vector2, scale: number): Vector2 {
    return { x: vec.x * scale, y: vec.y * scale };
}
export function Vec3Negate(a: Vector3, b: Vector3) {
    return new Vector3(a.x - b.x, a.y - b.y, a.z - b.z);
}
export function Vec3Add(a: Vector3, b: Vector3) {
    return new Vector3(a.x + b.x, a.y + b.y, a.z + b.z);
}
export function Vec3Multiply(vec: Vector3, scale: number) {
    return new Vector3(vec.x * scale, vec.y * scale, vec.z * scale);
}
export function Vec3Dot(a: Vector3, b: Vector3) {
    return a.x * b.x + a.y * b.y + a.z * b.z;
}
export function Vec3Cross(a: Vector3, b: Vector3): Vector3 {
    return new Vector3(a.y * b.z - a.z * b.y, a.z * b.x - a.x * b.z, a.x * b.y - a.y * b.x);
}

export function MathRotateRoll(vec: Vector3, angle: number) {
    const cachedX = vec.x;
    const cachedY = vec.y;
    vec.x = cachedX * Math.cos(angle) + cachedY * -Math.sin(angle);
    vec.y = cachedX * Math.sin(angle) + cachedY * Math.cos(angle);
}

export function MathMapToRange(t: number, t0: number, t1: number, newt0: number, newt1: number) {
    ///Translate to origin, scale by ranges ratio, translate to new position
    return (t - t0) * ((newt1 - newt0) / (t1 - t0)) + newt0;
}

export function MathMapToRangeClamped(t: number, t0: number, t1: number, newt0: number, newt1: number) {
    return MathClamp(MathMapToRange(t, t0, t1, newt0, newt1), newt0, newt1);
}

export function MathSmoothstep(edge0: number, edge1: number, x: number): number {
    // Scale, and clamp x to 0..1 range
    const t = Math.max(0, Math.min((x - edge0) / (edge1 - edge0), 1));
    // Evaluate polynomial
    return t * t * (3 - 2 * t);
}

export function MathLerp(start: number, end: number, t: number): number {
    return start * (1 - t) + end * t;
}
export function MathLerpVec2(start: Vector2, end: Vector2, t: number): Vector2 {
    return {
        x: MathLerp(start.x, end.x, t),
        y: MathLerp(start.y, end.y, t),
    };
}
export function MathLerpVec3(start: Vector3, end: Vector3, t: number): Vector3 {
    return new Vector3(MathLerp(start.x, end.x, t), MathLerp(start.y, end.y, t), MathLerp(start.z, end.z, t));
}
export function MathLerpColor(start: Color, end: Color, t: number): Color {
    return {
        r: MathLerp(start.r, end.r, t),
        g: MathLerp(start.g, end.g, t),
        b: MathLerp(start.b, end.b, t),
    };
}

export function MathHashf(n: number) {
    return (Math.sin(n) * 43758.5453) % 1;
}

export function MathIntersectionSphereSphere(pos1: Vector2, radius1: number, pos2: Vector2, radius2: number) {
    // Assuming spheres are objects with properties x, y, z (center) and radius
    const distance = Math.sqrt(Math.pow(pos1.x - pos2.x, 2) + Math.pow(pos1.y - pos2.y, 2));

    return distance <= radius1 + radius2;
}

export function MapSphericalToCartesian(theta: number, phi: number, r: number) {
    const vec = new Vector3(0, 0, 0);
    vec.x = Math.cos(phi) * Math.cos(theta) * r;
    vec.y = Math.sin(phi) * r;
    vec.z = Math.cos(phi) * Math.sin(theta) * r;
    return vec;
}

export function MathIntersectionSphereAABB(
    sphereCenter: Vector3,
    sphereRadius: number,
    aabbCenter: Vector3,
    aabbExtent: Vector3,
    outIntersectionPos: Vector3 | null = null,
): boolean {
    // Find the point on the AABB closest to the sphere center
    const closestPoint = GetVec3(
        Math.max(aabbCenter.x - aabbExtent.x, Math.min(sphereCenter.x, aabbCenter.x + aabbExtent.x)),
        Math.max(aabbCenter.y - aabbExtent.y, Math.min(sphereCenter.y, aabbCenter.y + aabbExtent.y)),
        Math.max(aabbCenter.z - aabbExtent.z, Math.min(sphereCenter.z, aabbCenter.z + aabbExtent.z)),
    );

    // Compute the vector from the sphere center to the closest point on the AABB
    const vecToClosestPoint = GetVec3(
        closestPoint.x - sphereCenter.x,
        closestPoint.y - sphereCenter.y,
        closestPoint.z - sphereCenter.z,
    );

    // Calculate the squared distance from the sphere center to the closest point
    const sqDist =
        vecToClosestPoint.x * vecToClosestPoint.x +
        vecToClosestPoint.y * vecToClosestPoint.y +
        vecToClosestPoint.z * vecToClosestPoint.z;

    // If the squared distance is less than or equal to the squared radius, there is an intersection
    const intersects = sqDist <= sphereRadius * sphereRadius;

    if (intersects && outIntersectionPos != null) {
        // The closest point on the AABB is the intersection point
        outIntersectionPos.x = closestPoint.x;
        outIntersectionPos.y = closestPoint.y;
        outIntersectionPos.z = closestPoint.z;
    }

    return intersects;
}

export function MathIntersectionRayPlane(
    rayOrigin: Vector3,
    rayDirection: Vector3,
    planePoint: Vector3,
    planeNormal: Vector3,
    outIntersectionPos: Vector3 | null = null,
) {
    // Calculate the dot product of the ray direction and plane normal
    const denom = rayDirection.x * planeNormal.x + rayDirection.y * planeNormal.y + rayDirection.z * planeNormal.z;

    // If the denominator is zero, the ray is parallel to the plane and doesn't intersect
    if (Math.abs(denom) < Number.EPSILON) {
        return false;
    }

    // Calculate the vector from the ray origin to the plane point
    const planeToRay = GetVec3(planePoint.x - rayOrigin.x, planePoint.y - rayOrigin.y, planePoint.z - rayOrigin.z);

    // Calculate the t value for the intersection point along the ray
    const t = (planeToRay.x * planeNormal.x + planeToRay.y * planeNormal.y + planeToRay.z * planeNormal.z) / denom;

    // If t is negative, the intersection point is behind the ray's origin
    if (t < 0) {
        return false;
    }

    // Calculate the intersection point
    if (outIntersectionPos != null) {
        outIntersectionPos.x = rayOrigin.x + t * rayDirection.x;
        outIntersectionPos.y = rayOrigin.y + t * rayDirection.y;
        outIntersectionPos.z = rayOrigin.z + t * rayDirection.z;
    }

    return true;
}

export function MathIntersectionRayAABB(
    rayOrigin: Vector3,
    rayDirection: Vector3,
    aabbCenter: Vector3,
    aabbExtent: Vector3,
    outIntersectionPos: Vector3 | null = null,
) {
    // Calculate the inverse direction of the ray
    const invDirection = GetVec3(1.0 / rayDirection.x, 1.0 / rayDirection.y, 1.0 / rayDirection.z);

    // Calculate the minimum and maximum t values for each axis
    const tmin = (aabbCenter.x - aabbExtent.x - rayOrigin.x) * invDirection.x;
    const tmax = (aabbCenter.x + aabbExtent.x - rayOrigin.x) * invDirection.x;
    const tymin = (aabbCenter.y - aabbExtent.y - rayOrigin.y) * invDirection.y;
    const tymax = (aabbCenter.y + aabbExtent.y - rayOrigin.y) * invDirection.y;
    const tzmin = (aabbCenter.z - aabbExtent.z - rayOrigin.z) * invDirection.z;
    const tzmax = (aabbCenter.z + aabbExtent.z - rayOrigin.z) * invDirection.z;

    // Find the maximum and minimum t values for intersection
    const tEnter = Math.max(Math.max(Math.min(tmin, tmax), Math.min(tymin, tymax)), Math.min(tzmin, tzmax));
    const tExit = Math.min(Math.min(Math.max(tmin, tmax), Math.max(tymin, tymax)), Math.max(tzmin, tzmax));

    // Check if the intersection is outside the valid range
    if (tExit < 0 || tEnter > tExit) {
        return false;
    } else {
        if (outIntersectionPos != null) {
            outIntersectionPos.x = rayOrigin.x + tEnter * rayDirection.x;
            outIntersectionPos.y = rayOrigin.y + tEnter * rayDirection.y;
            outIntersectionPos.z = rayOrigin.z + tEnter * rayDirection.z;
        }
        return true;
    }
}

export function MathIntersectionAABBSphere(
    sphereCenter: Vector3,
    sphereRadius: number,
    aabbCenter: Vector3,
    aabbExtent: Vector3,
): boolean {
    // Calculate the squared distance between the sphere center and the AABB
    const minDistX = Math.max(aabbCenter.x - aabbExtent.x, Math.min(sphereCenter.x, aabbCenter.x + aabbExtent.x));
    const minDistY = Math.max(aabbCenter.y - aabbExtent.y, Math.min(sphereCenter.y, aabbCenter.y + aabbExtent.y));
    const minDistZ = Math.max(aabbCenter.z - aabbExtent.z, Math.min(sphereCenter.z, aabbCenter.z + aabbExtent.z));

    const sqDistX = (minDistX - sphereCenter.x) * (minDistX - sphereCenter.x);
    const sqDistY = (minDistY - sphereCenter.y) * (minDistY - sphereCenter.y);
    const sqDistZ = (minDistZ - sphereCenter.z) * (minDistZ - sphereCenter.z);

    const sqDist = sqDistX + sqDistY + sqDistZ;

    // Check if the squared distance is less than the squared sphere radius
    return sqDist <= sphereRadius * sphereRadius;
}

export function uint16ToFloat16(uint16Value: number): number {
    // Extracting components
    const sign = (uint16Value & 0x8000) >> 15;
    const exponent = (uint16Value & 0x7c00) >> 10;
    const fraction = uint16Value & 0x03ff;

    // Reconstructing the float32 value
    let floatValue;

    if (exponent === 0) {
        if (fraction === 0) {
            // +/- 0
            floatValue = sign ? -0 : 0;
        } else {
            // Denormalized number
            floatValue = Math.pow(2, -14) * (fraction / Math.pow(2, 10)) * (sign ? -1 : 1);
        }
    } else if (exponent === 0x1f) {
        if (fraction === 0) {
            // +/- Infinity
            floatValue = sign ? -Infinity : Infinity;
        } else {
            // NaN
            floatValue = NaN;
        }
    } else {
        // Normalized number
        floatValue = Math.pow(2, exponent - 15) * (1 + fraction / Math.pow(2, 10)) * (sign ? -1 : 1);
    }

    return floatValue;
}

export function float32ToUint16(float32Value: number) {
    const floatView = new Float32Array(1);
    const intView = new Uint32Array(floatView.buffer);

    floatView[0] = float32Value;
    const x = intView[0];

    let bits = (x >> 16) & 0x8000; // Get the sign bit
    let m = (x >> 12) & 0x07ff; // Keep 11 bits of mantissa
    const e = (x >> 23) & 0xff; // Keep 8 bits of exponent

    if (e < 103) {
        // Too small to be represented as a normalized float16
        return bits;
    }

    if (e > 142) {
        // Too large to be represented as a float16, set to infinity
        bits |= 0x7c00;
        bits |= (e === 255 ? 1 : 0) && x & 0x007fffff; // Keep NaN (not infinity) bits
        return bits;
    }

    if (e < 113) {
        // Denormalized half-precision float
        m |= 0x0800;
        bits |= (m >> (114 - e)) + ((m >> (113 - e)) & 1);
        return bits;
    }

    bits |= ((e - 112) << 10) | (m >> 1);
    bits += m & 1;
    return bits;
}

export function SetPositionSmooth(
    inoutCurPos: Vector3,
    inoutCurVelocity: Vector3,
    desiredPos: Vector3,
    dTime: number,
    accelerationScale = 1.0,
    dampingScale = 1.0,
) {
    const curDiff = Vec3Negate(inoutCurPos, desiredPos);
    const acceleration = Vec3Negate(
        Vec3Multiply(curDiff, -accelerationScale),
        Vec3Multiply(inoutCurVelocity, dampingScale),
    );

    inoutCurVelocity.x += acceleration.x * dTime;
    inoutCurVelocity.y += acceleration.y * dTime;
    inoutCurVelocity.z += acceleration.z * dTime;

    inoutCurPos.x += inoutCurVelocity.x * dTime;
    inoutCurPos.y += inoutCurVelocity.y * dTime;
    inoutCurPos.z += inoutCurVelocity.z * dTime;
}

/* 
Time
*/
export const GTime = {
    Last: 0,
    Cur: 0.01,
    CurClamped: 0.0,
    Delta: 0.01,
    DeltaPrev: 0.01,
    DeltaSmooth: 0.01,
    DeltaMs: 1,
    DeltaTimeScale: 1.0, //For simulations
    //FPS counter:
    FrameTimesArr: [] as number[],
    CurFrameCursor: 0,
    NumFrames: 0,
    MaxFrames: 60,
    FPSTotal: 0,
    FPSAvrg: 0,
};
export function GetCurrentTimeSec() {
    return performance.now() / 1000.0;
}
export function UpdateTime() {
    GTime.Cur = GetCurrentTimeSec();
    GTime.DeltaPrev = MathLerp(GTime.Delta, GTime.DeltaPrev, 0.25);
    GTime.Delta = GTime.Cur - GTime.Last;

    //compute average FPS
    {
        const fps = 1 / GTime.Delta;
        // add the current fps and remove the oldest fps
        GTime.FPSTotal += fps - (GTime.FrameTimesArr[GTime.CurFrameCursor] || 0);
        // record the newest fps
        GTime.FrameTimesArr[GTime.CurFrameCursor++] = fps;
        // needed so the first N frames, before we have maxFrames, is correct.
        GTime.NumFrames = Math.max(GTime.NumFrames, GTime.CurFrameCursor);
        GTime.CurFrameCursor %= GTime.MaxFrames;
        GTime.FPSAvrg = GTime.FPSTotal / GTime.NumFrames;
    }
    if (GTime.Delta > 0.0) {
        GTime.Delta = MathClamp(GTime.Delta, 1.0 / 300.0, 1.0 / 30.0);
    }
    GTime.DeltaSmooth = MathLerp(GTime.DeltaPrev, GTime.Delta, 0.5);

    GTime.CurClamped += GTime.Delta;
    GTime.DeltaMs = GTime.Delta * 1000.0;
    GTime.Last = GTime.Cur;
}

export function GetMatrixBasisTransform(
    vertexA: Vector3,
    vertexB: Vector3,
    vertexC: Vector3,
    vertexD: Vector3,
): Matrix4x4 {
    // Calculate basis vectors
    const basisX = Vec3Negate(vertexD, vertexA);
    basisX.Normalize();
    const basisY = Vec3Negate(vertexB, vertexA);
    basisY.Normalize();

    const basisZ = Vec3Cross(basisY, basisX); // Change the order of basisX and basisY
    basisZ.Normalize();

    /* basisY.Set(MathVector3Cross(basisZ, basisX));
    basisY.Normalize(); */

    // Calculate center point
    const centerPoint = GetVec3(0, 0, 0);
    centerPoint.Add(vertexA);
    centerPoint.Add(vertexB);
    centerPoint.Add(vertexC);
    centerPoint.Add(vertexD);
    centerPoint.Mul(0.25);

    // Create rotation matrix
    const rotationMatrix = new Matrix4x4();
    rotationMatrix.setBasisVectors(basisX, basisY, basisZ);

    // Construct translation matrix
    rotationMatrix.setTranslation(centerPoint);

    return rotationMatrix;
}

export function GetMatrixInverse(mat: Matrix4x4) {
    const det = mat.getDeterminant();
    if (det === 0) return null; // Matrix is singular, cannot invert

    const invDet = 1 / det;

    const result = new Matrix4x4();
    result.elements[0][0] =
        invDet * (mat.elements[1][1] * mat.elements[2][2] - mat.elements[1][2] * mat.elements[2][1]);
    result.elements[0][1] =
        -invDet * (mat.elements[0][1] * mat.elements[2][2] - mat.elements[0][2] * mat.elements[2][1]);
    result.elements[0][2] =
        invDet * (mat.elements[0][1] * mat.elements[1][2] - mat.elements[0][2] * mat.elements[1][1]);
    result.elements[1][0] =
        -invDet * (mat.elements[1][0] * mat.elements[2][2] - mat.elements[1][2] * mat.elements[2][0]);
    result.elements[1][1] =
        invDet * (mat.elements[0][0] * mat.elements[2][2] - mat.elements[0][2] * mat.elements[2][0]);
    result.elements[1][2] =
        -invDet * (mat.elements[0][0] * mat.elements[1][2] - mat.elements[0][2] * mat.elements[1][0]);
    result.elements[2][0] =
        invDet * (mat.elements[1][0] * mat.elements[2][1] - mat.elements[1][1] * mat.elements[2][0]);
    result.elements[2][1] =
        -invDet * (mat.elements[0][0] * mat.elements[2][1] - mat.elements[0][1] * mat.elements[2][0]);
    result.elements[2][2] =
        invDet * (mat.elements[0][0] * mat.elements[1][1] - mat.elements[0][1] * mat.elements[1][0]);

    result.elements[0][3] =
        -result.elements[0][0] * mat.elements[0][3] -
        result.elements[0][1] * mat.elements[1][3] -
        result.elements[0][2] * mat.elements[2][3];
    result.elements[1][3] =
        -result.elements[1][0] * mat.elements[0][3] -
        result.elements[1][1] * mat.elements[1][3] -
        result.elements[1][2] * mat.elements[2][3];
    result.elements[2][3] =
        -result.elements[2][0] * mat.elements[0][3] -
        result.elements[2][1] * mat.elements[1][3] -
        result.elements[2][2] * mat.elements[2][3];

    return result;
}
export function GetMatrixTranspose(m: Matrix4x4) {
    const ret = new Matrix4x4();
    for (let r = 0; r < 4; r++) {
        for (let c = 0; c < 4; c++) {
            ret.elements[c][r] = m.elements[r][c];
        }
    }
    return ret;
}

export function ComputeLookToMatrix(position: Vector3, dir: Vector3, up: Vector3, inoutMat: Matrix4x4) {
    const R2 = dir;

    const R0 = GStack.TempVec3_3;
    R0.Set(up);
    R0.Cross(R2);
    R0.Normalize();

    const R1 = GStack.TempVec3_4;
    R1.Set(R2);
    R1.Cross(R0);
    R1.Normalize();

    /* basisY.Set(MathVector3Cross(basisZ, basisX));
    basisY.Normalize(); */

    inoutMat.setBasisVectors(R0, R1, R2);

    const newmat = GetMatrixTranspose(inoutMat);

    //const invPos = new Vector3(-position.x, -position.y, -position.z);
    const dotRes = new Vector3(0, 0, 0);
    dotRes.x = -Vec3Dot(R0, position);
    dotRes.y = -Vec3Dot(R1, position);
    dotRes.z = -Vec3Dot(R2, position);

    // Construct translation matrix
    inoutMat.elements = newmat.elements;
    inoutMat.setTranslation(dotRes);

    //console.log(inoutMat);
}

export function Vec3MulMatrix(vec: Vector3, mat: Matrix4x4, outRes: Vector3) {
    const x = vec.x;
    const y = vec.y;
    const z = vec.z;

    outRes.x = mat.elements[0][0] * x + mat.elements[0][1] * y + mat.elements[0][2] * z + mat.elements[0][3];
    outRes.y = mat.elements[1][0] * x + mat.elements[1][1] * y + mat.elements[1][2] * z + mat.elements[1][3];
    outRes.z = mat.elements[2][0] * x + mat.elements[2][1] * y + mat.elements[2][2] * z + mat.elements[2][3];
}

export function Vec4MulMatrix(vec: Vector3, mat: Matrix4x4) {
    const x = vec.x;
    const y = vec.y;
    const z = vec.z;

    const resultX = mat.elements[0][0] * x + mat.elements[1][0] * y + mat.elements[2][0] * z + mat.elements[3][0];
    const resultY = mat.elements[0][1] * x + mat.elements[1][1] * y + mat.elements[2][1] * z + mat.elements[3][1];
    const resultZ = mat.elements[0][2] * x + mat.elements[1][2] * y + mat.elements[2][2] * z + mat.elements[3][2];

    return new Vector3(resultX, resultY, resultZ);
}
